"use client";

import { useCallback, useMemo, useSyncExternalStore } from "react";
import { WalletReadyState } from "@solana/wallet-adapter-base";
import { PhantomWalletAdapter } from "@solana/wallet-adapter-phantom";

import {
  currentStandardWallet,
  standardWalletAdapter,
  subscribeStandardWallets,
} from "@/lib/wallet/standard-solana-wallet";

import type {
  Connection,
  SendOptions,
  VersionedTransaction,
} from "@solana/web3.js";

/* The same failure from a Wallet Standard wallet (Coinbase Wallet, Solflare,
   Backpack), which has no extension called Phantom to point at. */
export const WALLET_NOT_READY =
  "Your wallet could not connect. Unlock it and check a Solana account is selected, then press Connect again.";

const CANNOT_SIGN =
  "This wallet cannot sign for Mudwater. Connect Phantom instead.";

/**
 * The Solana adapter `usePhantomWallet` drives: Phantom's when Phantom is
 * here, otherwise a Wallet Standard wallet if one registered (the Coinbase
 * Wallet app's browser, Solflare, Backpack). With neither, Phantom's adapter
 * stays, so the install and phone paths behave exactly as they always have.
 */
export function useSolanaAdapter() {
  const phantom = useMemo(() => new PhantomWalletAdapter(), []);
  const subscribePhantom = useCallback(
    (onStoreChange: () => void) => {
      phantom.on("readyStateChange", onStoreChange);
      return () => void phantom.off("readyStateChange", onStoreChange);
    },
    [phantom]
  );
  const phantomInstalled = useSyncExternalStore(
    subscribePhantom,
    () => phantom.readyState === WalletReadyState.Installed,
    () => false
  );
  const standardWallet = useSyncExternalStore(
    subscribeStandardWallets,
    currentStandardWallet,
    () => null
  );
  const standard = useMemo(
    () => (standardWallet ? standardWalletAdapter(standardWallet) : null),
    [standardWallet]
  );
  const adapter = !phantomInstalled && standard ? standard : phantom;
  const isPhantom = adapter === phantom;
  /* Signing is optional on a Wallet Standard adapter; `compatibleStandardWallet`
     only admits wallets that offer both, so a missing method is a wallet that
     changed under us, said plainly rather than as a TypeError. */
  const signer = useMemo(
    () => ({
      connect: () => adapter.connect(),
      signMessage: (message: Uint8Array) => {
        if (!adapter.signMessage) throw new Error(CANNOT_SIGN);
        return adapter.signMessage(message);
      },
      signTransaction: <T extends VersionedTransaction>(transaction: T) => {
        if (!adapter.signTransaction) throw new Error(CANNOT_SIGN);
        return adapter.signTransaction(transaction);
      },
      sendTransaction: (
        transaction: VersionedTransaction,
        connection: Connection,
        options?: SendOptions
      ) => adapter.sendTransaction(transaction, connection, options),
    }),
    [adapter]
  );
  return { adapter, isPhantom, signer };
}
